import React, { useEffect, useMemo, useState } from "react";
import { api, formatError, SERVICES, CLIENT_TYPES, CUSTOMER_PAYMENT_STATUSES, PAYOUT_STATUSES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, AlertTriangle, TrendingUp, Wallet, Users, Lock, LogOut, ShieldCheck } from "lucide-react";
import { format, subDays } from "date-fns";

const REV_PASS_KEY = "trustline_revenue_pass";

const PAYOUT_COLOR = {
  "Paid": "bg-emerald-100 text-emerald-800",
  "Ready to Pay": "bg-blue-100 text-blue-800",
  "Ready for Review": "bg-indigo-100 text-indigo-800",
  "Customer Unpaid": "bg-amber-100 text-amber-800",
  "Not Ready": "bg-slate-100 text-slate-600",
  "On Hold": "bg-rose-100 text-rose-700",
  "Disputed": "bg-rose-200 text-rose-900",
};

const PAYMENT_COLOR = {
  "Paid": "bg-emerald-100 text-emerald-800",
  "Deposit Paid": "bg-emerald-50 text-emerald-700",
  "Partially Paid": "bg-amber-100 text-amber-800",
  "Invoice Sent": "bg-blue-100 text-blue-800",
  "Not Sent": "bg-slate-100 text-slate-600",
  "Overdue": "bg-rose-100 text-rose-700",
  "Refunded": "bg-purple-100 text-purple-800",
  "Cancelled": "bg-slate-200 text-slate-500",
};

export default function AdminRevenue() {
  const [revPass, setRevPass] = useState(() => sessionStorage.getItem(REV_PASS_KEY) || "");
  const [data, setData] = useState({ items: [], summary: {}, settings: {} });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: format(subDays(new Date(), 90), "yyyy-MM-dd"),
    date_to: format(new Date(), "yyyy-MM-dd"),
    service_type: "",
    client_type: "",
    payment_status: "",
    owner_payout_status: "",
    hallie_payout_status: "",
    vat_rate: "",
  });

  const load = async (token) => {
    const useToken = token ?? revPass;
    if (!useToken) return;
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/admin/revenue/jobs", {
        params,
        headers: { "X-Revenue-Pass": useToken },
      });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 423 || e?.response?.status === 401) {
        sessionStorage.removeItem(REV_PASS_KEY);
        setRevPass("");
        toast.error("Revenue access expired. Please re-enter the password.");
      } else {
        toast.error(formatError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (revPass) load(revPass);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const handleUnlocked = (token) => {
    sessionStorage.setItem(REV_PASS_KEY, token);
    setRevPass(token);
    load(token);
  };

  const lock = () => {
    sessionStorage.removeItem(REV_PASS_KEY);
    setRevPass("");
    setData({ items: [], summary: {}, settings: {} });
  };

  const apply = () => load();

  const reset = () => {
    setFilters({
      date_from: format(subDays(new Date(), 90), "yyyy-MM-dd"),
      date_to: format(new Date(), "yyyy-MM-dd"),
      service_type: "", client_type: "", payment_status: "",
      owner_payout_status: "", hallie_payout_status: "", vat_rate: "",
    });
    setTimeout(() => load(), 0);
  };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const token = localStorage.getItem("trustline_token");
      const resp = await fetch(`${apiBase}/admin/revenue/export.csv?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Revenue-Pass": revPass || "",
        },
      });
      if (resp.status === 423 || resp.status === 401) {
        toast.error("Revenue access expired. Re-enter the password.");
        lock();
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "trustline-revenue-share.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download CSV"); }
  };

  const hasWarnings = useMemo(
    () => (data.items || []).some((j) => j.revenue?.warnings?.length),
    [data.items]
  );

  if (!revPass) {
    return <RevenuePasswordGate onUnlocked={handleUnlocked} />;
  }

  const s = data.summary || {};

  return (
    <div className="space-y-6" data-testid="admin-revenue-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B] flex items-center gap-2">
            Revenue share
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 rounded-full px-2 py-0.5 font-semibold">
              <ShieldCheck className="w-3 h-3" /> Unlocked
            </span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Internal view only — never shown to customers, cleaners, or the public. Owner base rate: <b>€{data.settings?.owner_base_rate ?? 30}/h excl. VAT</b>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={lock} variant="outline" className="rounded-full" data-testid="revenue-lock-btn">
            <LogOut className="w-4 h-4 mr-2" /> Lock page
          </Button>
          <Button onClick={downloadCsv} variant="outline" className="rounded-full" data-testid="revenue-export-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={<TrendingUp className="w-4 h-4" />} label="Customer revenue (excl. VAT)" value={`€${(s.total_subtotal_excl_vat || 0).toFixed(2)}`} sub={`incl. VAT €${(s.total_incl_vat || 0).toFixed(2)}`} />
        <Tile icon={<TrendingUp className="w-4 h-4" />} label="VAT collected" value={`€${(s.total_vat || 0).toFixed(2)}`} sub="held for tax — not profit" muted />
        <Tile icon={<Wallet className="w-4 h-4" />} label="Owner share" value={`€${(s.total_owner_share || 0).toFixed(2)}`} sub={`Paid €${(s.total_owner_paid || 0).toFixed(2)} · Unpaid €${(s.total_owner_unpaid || 0).toFixed(2)}`} />
        <Tile icon={<Users className="w-4 h-4" />} label="Hallie share" value={`€${(s.total_hallie_share || 0).toFixed(2)}`} sub={`Paid €${(s.total_hallie_paid || 0).toFixed(2)} · Unpaid €${(s.total_hallie_unpaid || 0).toFixed(2)}`} green />
      </div>

      {s.total_unpaid_customer > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> <b>€{s.total_unpaid_customer.toFixed(2)}</b> still pending from customers across <b>{s.unpaid_jobs}</b> job(s). Owner & Hallie payouts will remain blocked until paid.
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl ring-1 ring-slate-200 p-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterField label="From"><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="rev-from" /></FilterField>
          <FilterField label="To"><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="rev-to" /></FilterField>
          <FilterField label="Service"><SelectSimple value={filters.service_type} onChange={(v) => setFilters({ ...filters, service_type: v })} options={SERVICES} placeholder="All services" /></FilterField>
          <FilterField label="Client type"><SelectSimple value={filters.client_type} onChange={(v) => setFilters({ ...filters, client_type: v })} options={CLIENT_TYPES} placeholder="All" /></FilterField>
          <FilterField label="Customer payment"><SelectSimple value={filters.payment_status} onChange={(v) => setFilters({ ...filters, payment_status: v })} options={CUSTOMER_PAYMENT_STATUSES} placeholder="All" /></FilterField>
          <FilterField label="Owner payout"><SelectSimple value={filters.owner_payout_status} onChange={(v) => setFilters({ ...filters, owner_payout_status: v })} options={PAYOUT_STATUSES} placeholder="All" /></FilterField>
          <FilterField label="Hallie payout"><SelectSimple value={filters.hallie_payout_status} onChange={(v) => setFilters({ ...filters, hallie_payout_status: v })} options={PAYOUT_STATUSES} placeholder="All" /></FilterField>
          <FilterField label="VAT rate %"><Input type="number" placeholder="e.g. 9 or 21" value={filters.vat_rate} onChange={(e) => setFilters({ ...filters, vat_rate: e.target.value })} /></FilterField>
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <Button variant="ghost" onClick={reset} data-testid="rev-reset">Reset</Button>
          <Button onClick={apply} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full" data-testid="rev-apply">Apply filters</Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wide">
              <tr>
                <th className="text-left px-3 py-3">Date</th>
                <th className="text-left px-3 py-3">Customer</th>
                <th className="text-left px-3 py-3">Service · Client</th>
                <th className="text-right px-3 py-3">Hrs</th>
                <th className="text-right px-3 py-3">€/h</th>
                <th className="text-right px-3 py-3">Sub excl.</th>
                <th className="text-right px-3 py-3">VAT %</th>
                <th className="text-right px-3 py-3">VAT €</th>
                <th className="text-right px-3 py-3">Total</th>
                <th className="text-right px-3 py-3">Owner</th>
                <th className="text-right px-3 py-3">Hallie</th>
                <th className="text-left px-3 py-3">Customer</th>
                <th className="text-left px-3 py-3">Owner</th>
                <th className="text-left px-3 py-3">Hallie</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={14} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={14} className="px-3 py-8 text-center text-slate-400">No jobs match the filters.</td></tr>}
              {!loading && data.items.map((j) => {
                const r = j.revenue || {};
                const hasWarning = (r.warnings?.length || 0) > 0;
                return (
                  <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`rev-row-${j.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{j.date}</td>
                    <td className="px-3 py-2 max-w-[180px] truncate">{j.customer_name}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#0F172A]">{j.service_type}</div>
                      <div className="text-[11px] text-slate-500">{r.client_type}</div>
                    </td>
                    <td className="px-3 py-2 text-right">{r.billable_hours?.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right">€{r.hourly_rate_excl_vat?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">€{r.subtotal_excl_vat?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{r.vat_rate}</td>
                    <td className="px-3 py-2 text-right text-slate-500">€{r.vat_amount?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#1B3A6B]">€{r.total_incl_vat?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">€{r.owner_share?.toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right ${r.hallie_share < 0 ? "text-rose-700 font-bold" : "text-slate-700"}`}>
                      {hasWarning && <AlertTriangle className="w-3 h-3 inline mr-1 text-rose-500" />}
                      €{r.hallie_share?.toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <StatusEditor
                        jobId={j.id}
                        field="payment_status"
                        value={r.customer_payment_status}
                        options={CUSTOMER_PAYMENT_STATUSES}
                        colorMap={PAYMENT_COLOR}
                        onSaved={() => load()}
                        testid={`rev-cust-status-${j.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <StatusEditor
                        jobId={j.id}
                        field="owner_payout_status"
                        value={r.owner_payout_status}
                        options={PAYOUT_STATUSES}
                        colorMap={PAYOUT_COLOR}
                        onSaved={() => load()}
                        testid={`rev-owner-status-${j.id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <StatusEditor
                        jobId={j.id}
                        field="hallie_payout_status"
                        value={r.hallie_payout_status}
                        options={PAYOUT_STATUSES}
                        colorMap={PAYOUT_COLOR}
                        onSaved={() => load()}
                        testid={`rev-hallie-status-${j.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hasWarnings && (
          <div className="px-4 py-2 bg-amber-50 text-xs text-amber-900 border-t border-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Rows with warning icons indicate negative Hallie share or hourly rate below owner base rate.
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 max-w-2xl">
        {data.settings?.outsourced_contractor_label || "Outsourced Operations / Business Support Fee"} — Hallie is treated as an outsourced contractor (Philippines).
        Trustline does not run payroll, social security, or salary deductions for this share.
      </p>
    </div>
  );
}

function RevenuePasswordGate({ onUnlocked }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setErr("");
    try {
      const r = await api.post("/admin/revenue/unlock", { password });
      if (r.data?.unlocked) {
        toast.success("Revenue share unlocked.");
        onUnlocked(r.data.token || password);
      } else {
        setErr("Incorrect password.");
      }
    } catch (e2) {
      setErr(formatError(e2) || "Incorrect password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="revenue-locked-gate">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white rounded-2xl ring-1 ring-slate-200 p-7 shadow-sm"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full bg-[#1B3A6B] text-white flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-[#1B3A6B]">Revenue share is locked</h2>
            <p className="text-xs text-slate-500">Enter the internal access password to view financial split data.</p>
          </div>
        </div>

        <Label htmlFor="revenue-pass" className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
          Access password
        </Label>
        <Input
          id="revenue-pass"
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-1.5"
          data-testid="revenue-password-input"
        />
        {err && <p className="text-sm text-rose-600 mt-2" data-testid="revenue-password-error">{err}</p>}

        <Button
          type="submit"
          disabled={submitting || !password.trim()}
          className="w-full mt-5 bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full"
          data-testid="revenue-password-submit"
        >
          {submitting ? "Verifying…" : "Unlock revenue share"}
        </Button>

        <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
          This view shows owner profit and contractor share. It is never displayed to customers, cleaners, or staff without this password. Access is remembered only for this browser tab.
        </p>
      </form>
    </div>
  );
}

function Tile({ icon, label, value, sub, muted, green }) {
  return (
    <div className={`rounded-xl p-4 ${green ? "bg-[#E8F5E0] ring-1 ring-[#5BA83F]/30" : muted ? "bg-slate-50 ring-1 ring-slate-200" : "bg-white ring-1 ring-slate-200"}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide font-semibold text-slate-500">{icon}{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${green ? "text-[#3F7A1F]" : "text-[#1B3A6B]"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SelectSimple({ value, onChange, options, placeholder }) {
  return (
    <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
      <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{placeholder || "All"}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function StatusEditor({ jobId, field, value, options, colorMap, onSaved, testid }) {
  const [current, setCurrent] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCurrent(value || ""); }, [value]);

  const handleChange = async (next) => {
    if (!next || next === current) return;
    const prev = current;
    setCurrent(next);
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}`, { [field]: next });
      toast.success("Status updated");
      onSaved?.();
    } catch (e) {
      setCurrent(prev);
      toast.error(formatError(e) || "Could not update status");
    } finally {
      setSaving(false);
    }
  };

  const badgeCls = colorMap[current] || "bg-slate-100 text-slate-600";
  const opts = options.includes(current) || !current ? options : [current, ...options];

  return (
    <Select value={current} onValueChange={handleChange} disabled={saving}>
      <SelectTrigger
        data-testid={testid}
        className={`h-7 px-2 py-0 border-0 rounded-full text-xs font-medium w-auto min-w-[130px] ${badgeCls} focus:ring-2 focus:ring-[#1B3A6B]/30 ${saving ? "opacity-60" : ""}`}
      >
        <SelectValue placeholder="Set status" />
      </SelectTrigger>
      <SelectContent>
        {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
